import { Dialog, DialogContent, Typography, DialogActions, useTheme, Autocomplete, TextField, Box, useMediaQuery } from '@mui/material';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ButtonComponent from 'src/components/ButtonComponent';
import { ExportModalBody, ExportHeaderRow, ExportTypeRow, DateWrapper } from 'src/style/common';
import Row from 'src/frontend-common/components/Row';
import BasicExportImg from 'src/components/ExportModal/basic-export.jpg';
import FullExportImg from 'src/components/ExportModal/full-export.jpg';
import AnalyticsSummaryImg from 'src/components/ExportModal/Radio_button_PDF.jpg';
import DateFilter from 'src/components/DateFilter';
import { Products } from 'src/frontend-common/dto/Types/ProductType';
import AppkeyAutocomplete from 'src/components/AppkeyAutocomplete';
export interface Props {
  open: boolean;
  onClose: () => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<number>>;
  setProductFilter?: React.Dispatch<React.SetStateAction<Products | 'all' | undefined>>;
  isLookups?: boolean;
  onSubmit: (appKey: string, startDate: Date, endDate: Date, product: Products | 'all' | undefined, is_full?: boolean, isAnalytics?: boolean) => void;
  search: string;
  scoreCluster: string | undefined;
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  fullExportSwitchHidden?: boolean;
}

export const ExportModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  isLookups,
  setActiveTab,
  setProductFilter,
  fullExportSwitchHidden = false,
  dateRange,
}) => {
  const theme = useTheme();
  const [selectedAppkey, setSelectedAppkey] = React.useState<{
    label: string;
    key: string;
  } | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<string | null>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isFull, setIsFull] = React.useState(false);

  const [isAnalytics, setIsAnalytics] = React.useState(false);
  const [exportDataRange, setExportDataRange] = React.useState<{
    startDate: Date;
    endDate: Date;
  }>(dateRange);

  const handleSubmit = () => {
    if (!selectedAppkey) return;
    if (isAnalytics && setActiveTab && setProductFilter && !selectedProduct) {
      setActiveTab(1);
      setProductFilter('analytics');
    } else if (!isAnalytics && setProductFilter && setActiveTab) {
      setActiveTab(0);
      switch (selectedProduct) {
        case 'Onboarding':
          setProductFilter(undefined);
          break;
        case 'Phone':
          setProductFilter('phone');
          break;
        case 'Email':
          setProductFilter('email');
          break;
        case 'IP Address':
          setProductFilter('ip');
          break;
        case 'Domain':
          setProductFilter('domain');
          break;
      }
    }
    onSubmit(
      selectedAppkey.key,
      exportDataRange.startDate,
      exportDataRange.endDate,
      selectedProduct === 'IP Address' ? 'ip' : selectedProduct === 'Onboarding' ? undefined : (selectedProduct?.toLowerCase() as Products),
      isFull,
      isAnalytics
    );
    setSelectedAppkey(null);
    setSelectedProduct(null);
    setIsFull(false);
    setIsAnalytics(false);
  };

  const setDateRange = (range: { startDate: Date; endDate: Date }) => {
    setExportDataRange(range);
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={isLookups ? 'sm' : 'md'}
      open={open}
      onClose={() => {
        onClose();
        setSelectedProduct(null);
        setIsFull(false);
        setIsAnalytics(false);
      }}
    >
      <ExportHeaderRow>
        <Typography variant="h4">Export</Typography>
        <ButtonComponent
          icon={<CloseIcon />}
          buttontype="icon"
          onClick={() => {
            onClose();
            setSelectedProduct(null);
            setIsFull(false);
            setIsAnalytics(false);
          }}
        />
      </ExportHeaderRow>

      <DialogContent style={{ padding: 24 }}>
        <ExportModalBody>
          <Typography variant="h4">App key</Typography>
          <>
            <AppkeyAutocomplete selectedAppkey={selectedAppkey} setSelectedAppkey={setSelectedAppkey} />
            <Typography variant="h4" style={{ marginTop: 24 }} data-testid="export-modal-test">
              Choose the type of export
            </Typography>
            {!fullExportSwitchHidden && (
              <ExportTypeRow isExport={1}>
                <button
                  onClick={() => {
                    setIsFull(false);
                    setIsAnalytics(false);
                  }}
                  style={{
                    display: 'flex',

                    justifyContent: 'space-between',
                  }}
                  className={!isFull && !isAnalytics ? 'active' : ''}
                >
                  <Row>
                    <>
                      {!isMobile && (
                        <Box marginRight="10px">
                          <>{!isFull && !isAnalytics && <Box />}</>
                        </Box>
                      )}
                    </>
                    <img src={BasicExportImg} style={{ width: isMobile ? '100%' : 197 }} width="100%" alt="Basic export" />
                  </Row>
                  <Typography variant="body2">CSV (basic)</Typography>
                </button>

                <button
                  onClick={() => {
                    setIsFull(true);
                    setIsAnalytics(false);
                  }}
                  className={isFull ? 'active' : ''}
                  style={{
                    display: 'flex',
                    marginLeft: 10,
                    justifyContent: 'space-between',
                  }}
                >
                  <Row>
                    <>
                      {!isMobile && (
                        <Box marginRight="10px">
                          <>{isFull && <Box />}</>
                        </Box>
                      )}
                    </>
                    <img src={FullExportImg} style={{ width: isMobile ? '100%' : 197 }} width="100%" alt="Full export" />
                  </Row>
                  <Typography variant="body2">CSV (advanced)</Typography>
                </button>

                <>
                  {!isLookups && (
                    <button
                      onClick={() => {
                        setIsAnalytics(true);
                        setIsFull(false);
                        setSelectedProduct(null);
                      }}
                      className={isAnalytics ? 'active' : ''}
                      style={{
                        display: 'flex',
                        marginLeft: 10,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Row>
                        <>
                          {!isMobile && (
                            <Box marginRight="10px">
                              <>{isAnalytics && <Box />}</>
                            </Box>
                          )}
                        </>
                        <img src={AnalyticsSummaryImg} style={{ width: isMobile ? '100%' : 197 }} width="100%" alt="Analytics summary" />
                      </Row>
                      <Typography variant="body2">PDF</Typography>
                    </button>
                  )}
                </>
              </ExportTypeRow>
            )}
            {!isAnalytics && (
              <>
                <Typography variant="h4">Product</Typography>
                <Autocomplete
                  style={{ marginBottom: 24 }}
                  disablePortal
                  id="app-key-select-list"
                  options={['Onboarding', 'Phone', 'Email', 'IP Address', 'Domain']}
                  autoHighlight
                  data-testid="test-app-key-select-list"
                  value={selectedProduct}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onInputChange={(_, newInputValue) => {
                    if (setProductFilter) {
                      setSelectedProduct(newInputValue);
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Product" fullWidth={true} />}
                />
              </>
            )}
            <Typography variant="h4">Date</Typography>
            <DateWrapper>
              <DateFilter
                dateRange={{
                  startDate: exportDataRange.startDate,
                  endDate: exportDataRange.endDate,
                }}
                setDateRange={(range) => {
                  setDateRange(range);
                }}
              />
            </DateWrapper>
          </>
        </ExportModalBody>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'row-reverse', borderTop: '1px solid #F5F5F5' }}>
        <ButtonComponent
          buttontype="cancel"
          onClick={() => {
            onClose();
            setSelectedProduct(null);
            setIsFull(false);
            setIsAnalytics(false);
          }}
        />
        <ButtonComponent
          text="Export"
          buttontype="mainaction"
          disabled={!selectedAppkey || (!isAnalytics && !selectedProduct)}
          onClick={handleSubmit}
          sx={{ marginRight: theme.spacing(2), marginLeft: `0px!important` }}
        />
      </DialogActions>
    </Dialog>
  );
};
